import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Star, TrendingUp, Eye, Users, Zap, Crown, Mail } from 'lucide-react';

const Advertising = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';

  const packages = isAr ? [
    { icon: Star, title: 'إعلان مميز', desc: 'يظهر إعلانك في أعلى نتائج البحث مع شارة "مميز" ذهبية لجذب المزيد من المشترين.', color: 'from-amber-500 to-orange-500' },
    { icon: TrendingUp, title: 'إعلان مدعوم', desc: 'يظهر إعلانك في الصفحة الرئيسية وصفحات الفئات لتحقيق أقصى وصول.', color: 'from-blue-500 to-cyan-500' },
    { icon: Crown, title: 'باقة التاجر', desc: 'اشتراك شهري للتجار يتضمن إعلانات مميزة غير محدودة وشارة تاجر موثق وأولوية في الظهور.', color: 'from-purple-500 to-pink-500' },
  ] : [
    { icon: Star, title: 'Featured Listing', desc: 'Your ad appears at the top of search results with a gold "Featured" badge to attract more buyers.', color: 'from-amber-500 to-orange-500' },
    { icon: TrendingUp, title: 'Sponsored Ad', desc: 'Your ad is displayed on the homepage and category pages for maximum visibility and reach.', color: 'from-blue-500 to-cyan-500' },
    { icon: Crown, title: 'Dealer Package', desc: 'Monthly subscription for dealers with unlimited featured listings, verified dealer badge, and priority placement.', color: 'from-purple-500 to-pink-500' },
  ];

  const stats = isAr ? [
    { icon: Users, value: '10K+', label: 'مستخدم نشط' },
    { icon: Eye, value: '50K+', label: 'مشاهدة يومية' },
    { icon: Zap, value: '5x', label: 'زيادة في المبيعات' },
  ] : [
    { icon: Users, value: '10K+', label: 'Active Users' },
    { icon: Eye, value: '50K+', label: 'Daily Views' },
    { icon: Zap, value: '5x', label: 'More Sales' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-32" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-2xl mx-auto px-4 pt-4 pb-6 relative z-10">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center hover:bg-white/15 transition-all active:scale-90 mb-4">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-extrabold text-white font-heading">
            {isAr ? 'أعلن معنا' : 'Advertise with Us'}
          </h1>
          <p className="text-white/40 text-sm mt-1">
            {isAr ? 'وصّل منتجاتك لآلاف المشترين في الإمارات' : 'Reach thousands of buyers across the UAE'}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2.5">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4 text-center">
                <Icon className="w-5 h-5 text-[#E31C25] mx-auto mb-2" />
                <p className="text-white font-extrabold text-lg">{stat.value}</p>
                <p className="text-white/40 text-[10px] font-semibold">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Packages */}
        <h2 className="text-white font-bold text-sm pt-2">
          {isAr ? 'باقات الإعلانات' : 'Advertising Packages'}
        </h2>
        {packages.map((pkg, i) => {
          const Icon = pkg.icon;
          return (
            <div key={i} className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] overflow-hidden">
              <div className={`h-1 bg-gradient-to-r ${pkg.color}`} />
              <div className="p-4 flex gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${pkg.color} rounded-xl flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm mb-1">{pkg.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{pkg.desc}</p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Why Advertise */}
        <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4">
          <h3 className="font-bold text-white text-sm mb-3">
            {isAr ? 'لماذا تعلن على فايند أدز؟' : 'Why Advertise on Findadz?'}
          </h3>
          <ul className="space-y-2">
            {(isAr ? [
              'جمهور مستهدف في جميع أنحاء الإمارات',
              'أسعار تنافسية مقارنة بالمنصات الأخرى',
              'تقارير أداء مفصلة لإعلاناتك',
              'دعم فني متواصل لتحقيق أفضل النتائج',
            ] : [
              'Targeted audience across all UAE emirates',
              'Competitive pricing compared to other platforms',
              'Detailed performance reports for your ads',
              'Dedicated support to maximize your results',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-[#E31C25] rounded-full mt-1.5 shrink-0" />
                <span className="text-white/50 text-xs">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Button */}
        <button
          onClick={() => window.open('mailto:findadz@hotmail.com?subject=Advertising Inquiry&body=' + encodeURIComponent(isAr ? 'مرحبًا، أود الاستفسار عن باقات الإعلانات المتاحة.' : 'Hello, I would like to inquire about your advertising packages.'))}
          className="w-full py-3.5 bg-[#E31C25] text-white rounded-2xl font-bold text-sm hover:bg-[#C41820] transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98] flex items-center justify-center gap-2"
          data-testid="contact-advertising">
          <Mail className="w-4 h-4" />
          {isAr ? 'تواصل معنا للإعلان' : 'Contact Us for Advertising'}
        </button>
        <p className="text-center text-white/30 text-xs">findadz@hotmail.com</p>
      </div>
    </div>
  );
};

export default Advertising;
